import { addSimulationSetWithSimulations, replaceSimulationSetWithSimulations } from 'api/simulationSetApi';
import type { SimulationInsert } from '@urbanmetrix/sdk';
import type { GeodataTarget } from 'contexts/GeodataContext';
import type { SubSimulation, SimulationState } from 'contexts/SimulationContext';
import {
  DEFAULT_NOISE_SOURCE_DISTANCE,
  DEFAULT_NUMBER_OF_REFLECTIONS,
  MAX_NOISE_SOURCE_DISTANCE_WITH_REFLECTION_AND_HORIZONTAL_DIFFRACTION,
} from 'constants/simulation';
import { enrichBorderCoordinatesWithHeight } from 'services/gridHeightEnrichment';
import { normalizeWindMeshDistanceByLength2 } from 'services/normalizeWindMeshDistance';
import { hydrateWindSubSimulationsFromGrid } from 'services/windParamsHydration';
import { isShadowVolumeConfigured, type ResidentialArea } from '../shadow/shadowResidentialAreas';

type SimulationTypeValue = NonNullable<SimulationInsert['simType']>;
type CreatedSimulationPayload = {
  id?: string | number;
  name?: string | null;
  simType?: SubSimulation['simType'];
  status?: string | null;
  params?: Record<string, unknown> | null;
};
type CreatedSimulationSetPayload = {
  id?: string | number;
  simulations?: CreatedSimulationPayload[];
  [key: string]: unknown;
};
type LooseSubSimulation = SubSimulation & { [key: string]: unknown };
type TargetType = 'LAKE' | 'RIVER' | 'FOREST' | 'CUSTOM';
type TargetPolygonCoordinate = { x: number; y: number };
type TargetPolygonPayload = TargetPolygonCoordinate[][];
type TargetMultiPolygonPayload = TargetPolygonPayload[];
type TargetPayloadCandidate = GeodataTarget;

const noiseFilters = [
  'motorway',
  'motorway_link',
  'trunk',
  'primary',
  'secondary',
  'tertiary',
  'unclassified',
  'residential',
  'service',
];

const toNumberOrNull = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumberOrDefault = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeTargetType = (value: unknown): TargetType | null => {
  switch (String(value ?? '').toUpperCase()) {
    case 'LAKE':
    case 'RIVER':
    case 'FOREST':
    case 'CUSTOM':
      return String(value).toUpperCase() as TargetType;
    default:
      return null;
  }
};

const normalizePolygonPayload = (polygon: unknown): TargetPolygonPayload | null => {
  if (!Array.isArray(polygon)) {
    return null;
  }

  const normalizeRing = (ring: unknown): TargetPolygonCoordinate[] | null => {
    if (!Array.isArray(ring)) {
      return null;
    }

    const coordinates = ring
      .map((coordinate) => {
        const point = coordinate as { x?: unknown; y?: unknown };
        const x = Number(point?.x);
        const y = Number(point?.y);
        return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
      })
      .filter((coordinate): coordinate is TargetPolygonCoordinate => Boolean(coordinate));

    if (coordinates.length === 0) {
      return null;
    }

    const firstPoint = coordinates[0];
    const lastPoint = coordinates[coordinates.length - 1];

    if (Number(firstPoint?.x) !== Number(lastPoint?.x) || Number(firstPoint?.y) !== Number(lastPoint?.y)) {
      return [...coordinates, { ...firstPoint }];
    }

    return coordinates;
  };

  const firstItem = polygon[0];

  if (!Array.isArray(firstItem)) {
    const ring = normalizeRing(polygon);
    return ring ? [ring] : null;
  }

  const firstNestedItem = firstItem[0];

  if (!Array.isArray(firstNestedItem)) {
    const rings = polygon
      .map((ring) => normalizeRing(ring))
      .filter((ring): ring is TargetPolygonCoordinate[] => Boolean(ring));
    return rings.length > 0 ? rings : null;
  }

  const firstPolygon = polygon
    .map((ring) => normalizeRing(Array.isArray(ring) ? ring[0] : ring))
    .filter((ring): ring is TargetPolygonCoordinate[] => Boolean(ring));
  return firstPolygon.length > 0 ? firstPolygon : null;
};

const normalizeMultiPolygonPayload = (polygon: unknown): TargetMultiPolygonPayload | null => {
  if (!Array.isArray(polygon)) {
    return null;
  }

  const firstItem = polygon[0];
  const firstNestedItem = Array.isArray(firstItem) ? firstItem[0] : null;

  if (Array.isArray(firstNestedItem)) {
    const polygons = polygon
      .map((entry) => normalizePolygonPayload(entry))
      .filter((entry): entry is TargetPolygonPayload => Boolean(entry));
    return polygons.length > 0 ? polygons : null;
  }

  const normalizedPolygon = normalizePolygonPayload(polygon);
  return normalizedPolygon ? [normalizedPolygon] : null;
};

const getTargetIdentityKey = (target: TargetPayloadCandidate): string | null => {
  const targetType = normalizeTargetType(target?.type);
  if (!targetType) {
    return null;
  }

  const targetId = Number(target?.id);
  if (Number.isFinite(targetId)) {
    return `id:${targetId}`;
  }

  return `${targetType}:${String(target?.name ?? '')}:${JSON.stringify(normalizePolygonPayload(target?.polygon))}`;
};

const dedupeTargets = (targets: TargetPayloadCandidate[]): GeodataTarget[] => {
  const targetsByKey = new Map<string, GeodataTarget>();

  targets.forEach((target) => {
    const key = getTargetIdentityKey(target);
    if (!key) {
      return;
    }

    const existingTarget = targetsByKey.get(key);
    const shouldReplaceExisting =
      !existingTarget ||
      (normalizePolygonPayload(existingTarget?.polygon) == null && normalizePolygonPayload(target?.polygon) != null);

    if (shouldReplaceExisting) {
      targetsByKey.set(key, target);
    }
  });

  return [...targetsByKey.values()];
};

const getHydratedSubSimulations = (
  simulation: SimulationState,
  gridData: { params?: Record<string, unknown> | null } | null | undefined,
  lengthMid: unknown
) =>
  normalizeWindMeshDistanceByLength2(
    hydrateWindSubSimulationsFromGrid((simulation?.subSimulations || []) as LooseSubSimulation[], gridData ?? {}),
    Number(lengthMid ?? NaN)
  );

const getTargetPayloads = (subSimulation: SubSimulation): GeodataTarget[] => {
  const explicitTargets = Array.isArray(subSimulation?.params?.targets)
    ? (subSimulation.params.targets as GeodataTarget[])
    : [];

  if (explicitTargets.length > 0) {
    return dedupeTargets(explicitTargets);
  }

  const targetType = normalizeTargetType(subSimulation?.params?.type);
  if (!targetType || typeof subSimulation?.params?.name !== 'string') {
    return [];
  }

  return [
    {
      type: targetType,
      name: subSimulation.params.name,
      radius: toNumberOrNull(subSimulation.params.radius),
      minArea: toNumberOrNull(subSimulation.params.minArea),
      polygon: normalizePolygonPayload(subSimulation.params.polygon) as unknown[] | null,
    },
  ];
};

const expandTargetViewSubSimulations = (
  subSimulation: SubSimulation,
  availableTargets: GeodataTarget[] = []
): SubSimulation[] => {
  if (subSimulation?.simType !== 'TARGETVIEW') {
    return [subSimulation];
  }

  if (!subSimulation?.params?.enabled) {
    return [];
  }

  const targets = getResolvedTargetPayloads(subSimulation, availableTargets);
  if (targets.length === 0) {
    return [];
  }

  const targetTypes = [...new Set(targets.map((target) => normalizeTargetType(target?.type)).filter(Boolean))];
  const isCustomTargetView =
    Boolean(subSimulation?.params?.customPolygonView) || (targetTypes.length === 1 && targetTypes[0] === 'CUSTOM');

  if (isCustomTargetView) {
    const polygons = targets
      .map((target) => normalizePolygonPayload(target?.polygon))
      .filter((polygon): polygon is TargetPolygonPayload => Boolean(polygon));

    return [
      {
        ...subSimulation,
        params: {
          enabled: true,
          desiredCellCount: subSimulation?.params?.desiredCellCount,
          type: 'CUSTOM',
          name: subSimulation.name || 'Custom Simulation',
          minArea: null,
          radius: null,
          polygon: polygons.length > 0 ? polygons : null,
        },
      },
    ];
  }

  return targets
    .map((target, index) => {
      const targetType = normalizeTargetType(target?.type);
      if (!targetType) {
        return null;
      }

      const suffix = targets.length > 1 ? ` ${index + 1}` : '';
      const nextSubSimulation: SubSimulation = {
        ...subSimulation,
        id: Number(subSimulation.id) * 1000 + index,
        name: targets.length > 1 ? `${subSimulation.name || 'Target View'}${suffix}` : subSimulation.name,
        params: {
          enabled: true,
          desiredCellCount: subSimulation?.params?.desiredCellCount,
          type: targetType,
          name:
            typeof target?.name === 'string' && target.name.trim().length > 0
              ? target.name
              : subSimulation.name || 'Target View',
          minArea: target?.minArea ?? null,
          radius: target?.radius ?? null,
          polygon: normalizeMultiPolygonPayload(target?.polygon),
        },
      };

      return nextSubSimulation;
    })
    .filter((value): value is SubSimulation => Boolean(value));
};

type PrepareSimulationForSubmissionArgs = {
  simulation: SimulationState;
  gridData?: { params?: Record<string, unknown> | null } | null;
  lengthMid: unknown;
  availableTargets?: GeodataTarget[];
};

const buildPreparedSimulation = (
  simulation: SimulationState,
  gridData: { params?: Record<string, unknown> | null } | null | undefined,
  lengthMid: unknown,
  availableTargets: GeodataTarget[] = []
): SimulationState => ({
  ...simulation,
  subSimulations: getHydratedSubSimulations(simulation, gridData, lengthMid).map((subSimulation) => {
    if (subSimulation?.simType !== 'TARGETVIEW') {
      return subSimulation;
    }

    const targets = getResolvedTargetPayloads(subSimulation, availableTargets);
    return {
      ...subSimulation,
      params: {
        ...subSimulation.params,
        targets,
      },
    };
  }),
});

const collapseShadowSubSimulations = (subSimulations: SubSimulation[]) => {
  const shadowSubSimulations = subSimulations.filter(({ simType }) => simType === 'SHADOW');
  if (shadowSubSimulations.length === 0) return subSimulations;

  const primaryShadow =
    shadowSubSimulations.find(({ params }) => isShadowVolumeConfigured(params)) ?? shadowSubSimulations[0];
  const shadowRule = primaryShadow.params.shadowRule ?? 'ZH';
  const volumes = shadowSubSimulations
    .filter(({ params }) => isShadowVolumeConfigured(params))
    .sort((first, second) => Number(first.params.analysisAreaIndex ?? 0) - Number(second.params.analysisAreaIndex ?? 0))
    .map(({ params }) => {
      const analysisAreaIndex = Number(params.analysisAreaIndex ?? 0);
      const residentialAreas = Array.isArray(params.residentialAreas)
        ? (params.residentialAreas as ResidentialArea[])
        : [];

      return {
        analysisAreaIndex,
        ...(shadowRule === 'ZH'
          ? {
              normalArea: normalizePolygonPayload(params.normalBuildableArea)?.[0] ?? [],
              normalMaximalHeight: toNumberOrDefault(params.normalMaximalHeight, 9),
            }
          : {}),
        buildableResidentialAreas: residentialAreas.flatMap((area) => {
          const coordinates = normalizePolygonPayload(area.coordinates)?.[0];
          return coordinates
            ? [
                {
                  coordinates,
                  minElevation: toNumberOrDefault(area.minElevation, 0),
                  maxElevation: toNumberOrDefault(area.maxElevation, 10),
                },
              ]
            : [];
        }),
      };
    });

  return subSimulations.flatMap((subSimulation) => {
    if (subSimulation.simType !== 'SHADOW') return [subSimulation];
    if (subSimulation !== primaryShadow) return [];
    return [{ ...subSimulation, params: { ...subSimulation.params, shadowRule, volumes } }];
  });
};

const getRunnablePreparedSubSimulations = (simulation: SimulationState, availableTargets: GeodataTarget[] = []) =>
  collapseShadowSubSimulations(
    simulation.subSimulations.flatMap((subSim) => expandTargetViewSubSimulations(subSim, availableTargets))
  );

const buildTargetsById = (targets: GeodataTarget[] = []) => {
  const targetsById = new Map<number, GeodataTarget>();
  targets.forEach((target) => {
    const targetId = Number(target?.id);
    if (Number.isFinite(targetId)) {
      targetsById.set(targetId, target);
    }
  });
  return targetsById;
};

const getResolvedTargetPayloads = (subSimulation: SubSimulation, availableTargets: GeodataTarget[] = []) => {
  const explicitTargets = getTargetPayloads(subSimulation);
  const targetsById = buildTargetsById(availableTargets);
  const overriddenRadius = toNumberOrNull(subSimulation?.params?.radius);
  const overriddenMinArea = toNumberOrNull(subSimulation?.params?.minArea);
  const targetIds = Array.isArray(subSimulation?.params?.targetIds)
    ? subSimulation.params.targetIds
        .map((value: unknown) => Number(value))
        .filter((value): value is number => Number.isFinite(value))
    : [];
  const resolvedTargets = targetIds
    .map((targetId) => targetsById.get(targetId))
    .filter((target): target is GeodataTarget => Boolean(target));
  return dedupeTargets([...explicitTargets, ...resolvedTargets]).map((target) => {
    const targetType = normalizeTargetType(target?.type);
    if (!targetType || targetType === 'CUSTOM') {
      return target;
    }

    return {
      ...target,
      radius: overriddenRadius ?? target?.radius ?? null,
      minArea: targetType === 'FOREST' ? (overriddenMinArea ?? target?.minArea ?? null) : (target?.minArea ?? null),
    };
  });
};

export const prepareSimulationForSubmission = ({
  simulation,
  gridData,
  lengthMid,
  availableTargets = [],
}: PrepareSimulationForSubmissionArgs) => ({
  simulation: buildPreparedSimulation(simulation, gridData, lengthMid, availableTargets),
});

export const buildSimulationParamsPayload = (
  subSimulation: SubSimulation | null | undefined,
  maxNoiseSourceDistance?: number
) => {
  const params: Record<string, unknown> = subSimulation?.params ?? {};

  switch (subSimulation?.simType) {
    case 'SUN':
      return {
        frameDivision: params?.frameDivision ?? 'YEARLY',
        raysEveryNMonths: Math.round(toNumberOrDefault(params?.raysEveryNMonths, 1)),
        raysEveryNDays: Math.round(toNumberOrDefault(params?.raysEveryNDays, 2)),
        raysEveryNHours: Math.round(toNumberOrDefault(params?.raysEveryNHours, 1)),
        raysEveryNMinutes: Math.round(toNumberOrDefault(params?.raysEveryNMinutes, 30)),
      };
    case 'FREEVIEW':
      return {
        decompose: Boolean(params?.decompose),
        raysLength: Math.round(toNumberOrDefault(params?.raysLength, 100)),
        raysResFactor: Math.round(toNumberOrDefault(params?.raysResFactor, 50)),
        outward: Boolean(params?.outward),
        angUp: Math.round(toNumberOrDefault(params?.angUp, 0)),
        angDown: Math.round(toNumberOrDefault(params?.angDown, 0)),
      };
    case 'TARGETVIEW':
      return {
        desiredCellCount: Math.round(clampNumber(toNumberOrDefault(params?.desiredCellCount, 2000), 100, 10000)),
        type: params?.type,
        name: params?.name,
        minArea: params?.minArea ?? null,
        radius: params?.radius ?? null,
        polygon: normalizeMultiPolygonPayload(params?.polygon),
      };
    case 'NOISE': {
      const filters = Array.isArray(params?.filters) ? params.filters : [];
      const maxSourceDistance = toNumberOrDefault(params?.maxSourceDistance, DEFAULT_NOISE_SOURCE_DISTANCE);
      const numberOfReflectionForLimit = toNumberOrDefault(params?.numberOfReflection, DEFAULT_NUMBER_OF_REFLECTIONS);
      const effectiveMaxNoiseSourceDistance =
        maxNoiseSourceDistance != null &&
        numberOfReflectionForLimit > DEFAULT_NUMBER_OF_REFLECTIONS &&
        Boolean(params?.horizontalDiffraction)
          ? Math.min(maxNoiseSourceDistance, MAX_NOISE_SOURCE_DISTANCE_WITH_REFLECTION_AND_HORIZONTAL_DIFFRACTION)
          : maxNoiseSourceDistance;
      return {
        maxSourceDistance: Math.round(
          effectiveMaxNoiseSourceDistance == null
            ? maxSourceDistance
            : Math.min(maxSourceDistance, effectiveMaxNoiseSourceDistance)
        ),
        lineSourceSpacingRatio: toNumberOrDefault(params?.lineSourceSpacingRatio, 2),
        numberOfReflection: Math.round(toNumberOrDefault(params?.numberOfReflection, 2)),
        maxReflectionDistance: Math.round(toNumberOrDefault(params?.maxReflectionDistance, 100)),
        horizontalDiffraction: Boolean(params?.horizontalDiffraction),
        verticalDiffraction: Boolean(params?.verticalDiffraction),
        railways: Boolean(params?.railways),
        filters: noiseFilters.every((filter) => filters.includes(filter))
          ? []
          : [...new Set(filters.flatMap((filter) => (filter === 'motorway' ? [filter, 'motorway_link'] : [filter])))],
      };
    }
    case 'WIND':
      return {
        meshDistance: toNumberOrNull(params?.meshDistance),
        surfaceRoughness: toNumberOrNull(params?.surfaceRoughness),
        windAverageBuildingHeight: toNumberOrNull(params?.windAverageBuildingHeight),
        referenceHeight: toNumberOrNull(params?.referenceHeight),
        windSimulationAccuracy: params?.windSimulationAccuracy ?? 'MEDIUM',
        referenceVelocity: toNumberOrNull(params?.referenceVelocity),
        flowDir: toNumberOrNull(params?.flowDir),
      };
    case 'SHADOW':
      return {
        shadowRule: params?.shadowRule ?? 'ZH',
        volumes: Array.isArray(params?.volumes) ? params.volumes : [],
      };
    default:
      return {};
  }
};

export const getRunnableSubSimulations = (
  simulation: SimulationState,
  gridData: { params?: Record<string, unknown> | null } | null | undefined,
  lengthMid: unknown,
  availableTargets: GeodataTarget[] = []
) =>
  getRunnablePreparedSubSimulations(
    buildPreparedSimulation(simulation, gridData, lengthMid, availableTargets),
    availableTargets
  );

type SubmitSimulationSetArgs = {
  geodataId: string;
  simulationSetId?: string | null;
  simulation: SimulationState;
  gridData?: { params?: Record<string, unknown> | null } | null;
  lengthMid: unknown;
  resolutionMid?: unknown;
  countsTowardUsage?: boolean;
  availableTargets?: GeodataTarget[];
};

export const submitSimulationSet = async ({
  geodataId,
  simulationSetId,
  simulation,
  gridData,
  lengthMid,
  resolutionMid,
  countsTowardUsage = true,
  availableTargets = [],
}: SubmitSimulationSetArgs): Promise<CreatedSimulationSetPayload & { subSimulations: SubSimulation[] }> => {
  const preparedSimulation = buildPreparedSimulation(simulation, gridData, lengthMid, availableTargets);
  const runnableSubSimulations = getRunnablePreparedSubSimulations(preparedSimulation, availableTargets);
  const midTerrainLength = toNumberOrNull(lengthMid);
  const midTerrainResolution = toNumberOrNull(resolutionMid);
  const maxNoiseSourceDistance =
    midTerrainLength == null || midTerrainResolution == null
      ? undefined
      : Math.max(0, midTerrainLength - midTerrainResolution);

  if (runnableSubSimulations.length === 0) {
    throw new Error('No runnable simulations selected');
  }

  const hasSimulationType = (simType: SubSimulation['simType']) =>
    runnableSubSimulations.some((subSimulation) => subSimulation.simType === simType);

  const payload = {
    geodataId: String(geodataId),
    name: preparedSimulation?.name?.trim() || 'Simulation Set',
    description: preparedSimulation?.description || null,
    solarEnabled: hasSimulationType('SUN'),
    freeViewEnabled: hasSimulationType('FREEVIEW'),
    targetViewEnabled: hasSimulationType('TARGETVIEW'),
    noiseEnabled: hasSimulationType('NOISE'),
    windEnabled: hasSimulationType('WIND'),
    shadowEnabled: hasSimulationType('SHADOW'),
    simulations: await Promise.all(
      runnableSubSimulations.map(async (subSimulation) => {
        const params = buildSimulationParamsPayload(subSimulation, maxNoiseSourceDistance);
        if (subSimulation.simType === 'SHADOW' && params.volumes) {
          params.volumes = await Promise.all(
            params.volumes.map(async (volume) => ({
              ...volume,
              ...(volume.normalArea ? { normalArea: await enrichBorderCoordinatesWithHeight(volume.normalArea) } : {}),
              buildableResidentialAreas: await Promise.all(
                volume.buildableResidentialAreas.map(async (area: { coordinates: TargetPolygonCoordinate[] }) => ({
                  ...area,
                  coordinates: await enrichBorderCoordinatesWithHeight(area.coordinates),
                }))
              ),
            }))
          );
        }
        return {
          id: Math.round(toNumberOrDefault(subSimulation?.id, 0)),
          name: subSimulation?.name || null,
          simType: (subSimulation?.simType ?? null) as SimulationTypeValue,
          countsTowardUsage,
          params,
        } as SimulationInsert;
      })
    ),
  };
  const createdSimulationSet = simulationSetId
    ? await replaceSimulationSetWithSimulations(simulationSetId, payload)
    : await addSimulationSetWithSimulations(payload);

  const normalizedCreatedSimulationSet = (createdSimulationSet ?? {}) as CreatedSimulationSetPayload;

  return {
    ...normalizedCreatedSimulationSet,
    subSimulations: (normalizedCreatedSimulationSet.simulations ?? []).map(
      (createdSubSimulation: CreatedSimulationPayload) => ({
        id: Number(createdSubSimulation?.id ?? 0),
        name: createdSubSimulation?.name ?? undefined,
        simType: createdSubSimulation?.simType ?? undefined,
        status: createdSubSimulation?.status ?? undefined,
        params: (createdSubSimulation?.params as Record<string, unknown>) ?? {},
      })
    ),
  };
};
